import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../db';
import { toast, confirmDialog } from '../utils/feedback';
import { Trash2, ChevronDown, ChevronUp, History as HistoryIcon } from 'lucide-react';
import { isCardioExercise, getCardioType, getCardioFields, calculateWorkoutVolume } from '../utils/exerciseUtils';
import Loader from '../components/Loader';

const History = ({ currentProfile }) => {
  const [workouts, setWorkouts] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWorkouts, setExpandedWorkouts] = useState({});

  const toggleWorkout = (id) => {
    setExpandedWorkouts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (currentProfile) loadWorkouts();
  }, [currentProfile?.id]);

  const loadWorkouts = async () => {
    setLoading(true);
    const [data, exercisesData] = await Promise.all([
      db.getWorkouts(currentProfile.id),
      db.getExercises(currentProfile.id)
    ]);
    setWorkouts(data);
    setAllExercises(exercisesData);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    confirmDialog('Veux-tu vraiment supprimer cette séance ?', async () => {
      await db.deleteWorkout(id);
      loadWorkouts();
      toast.success("Séance supprimée.");
    });
  };

  if (loading) return <Loader message="Chargement de l'historique..." />;

  return (
    <div>
      <h2>Historique des Séances</h2>
      
      {workouts.length === 0 ? (
        <div className="card text-center" style={{ padding: '3rem 1rem', opacity: 0.7, marginTop: '2rem' }}>
          <HistoryIcon size={48} style={{ margin: '0 auto 1rem auto', color: 'var(--text-secondary)' }} />
          <p style={{ fontSize: '1.2rem', color: 'var(--primary-color)', marginBottom: '0.5rem' }}>Ton historique est vide</p>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '300px', margin: '0 auto' }}>Il est temps d'aller transpirer un peu et de remplir ce journal !</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {workouts.map(workout => (
            <div key={workout.id} className="card">
              <div 
                className="flex justify-between items-center" 
                style={{ cursor: 'pointer', userSelect: 'none', marginBottom: expandedWorkouts[workout.id] ? '1rem' : '0' }}
                onClick={() => toggleWorkout(workout.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {expandedWorkouts[workout.id] ? <ChevronUp size={20} color="var(--text-secondary)" /> : <ChevronDown size={20} color="var(--text-secondary)" />}
                  <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>
                    {new Date(workout.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h3>
                </div>
                <button 
                  className="btn-icon" 
                  onClick={(e) => { e.stopPropagation(); handleDelete(workout.id); }} 
                  style={{color: 'var(--danger-color)'}} 
                  title="Supprimer"
                >
                  <Trash2 size={20} />
                </button>
              </div>

              {/* Tonnage calculation officiel (hors échauffement et cardio, support unilatéral) */}
              {(() => {
                const tonnage = workout.totalVolume !== undefined 
                  ? workout.totalVolume 
                  : calculateWorkoutVolume(workout, { allExercises });
                if (tonnage > 0) {
                  return (
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--secondary-color)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      🏋️ Tonnage Total : {tonnage.toLocaleString('fr-FR')} kg
                    </div>
                  );
                }
                return null;
              })()}

              {/* Muscle Badges */}
              {workout.muscles && workout.muscles.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                  {workout.muscles.map((muscle, i) => (
                    <span key={i} style={{ 
                      fontSize: '0.75rem', 
                      backgroundColor: 'var(--border-color)', 
                      color: 'var(--text-primary)',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '12px'
                    }}>
                      {muscle}
                    </span>
                  ))}
                </div>
              )}

              {(workout.feeling || workout.restTime || workout.duration) && (
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem', backgroundColor: 'var(--bg-color)', padding: '0.5rem 1rem', borderRadius: 'var(--border-radius-sm)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {workout.feeling && <span><strong>Ressenti:</strong> {workout.feeling === 'easy' ? '😊 Facile' : workout.feeling === 'normal' ? '😐 Normal' : workout.feeling === 'hard' ? '🥵 Difficile' : '💀 Extrême'}</span>}
                  {workout.restTime && <span><strong>Repos moyen:</strong> {workout.restTime} min</span>}
                  {workout.duration && <span><strong>⏱ Durée:</strong> {workout.duration >= 60 ? `${Math.floor(workout.duration / 60)}h${workout.duration % 60 > 0 ? (workout.duration % 60).toString().padStart(2, '0') : ''}` : `${workout.duration} min`}</span>}
                </div>
              )}
              
              {expandedWorkouts[workout.id] && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
                
                {workout.aiInsight && (
                  <div style={{
                    backgroundColor: 'rgba(168, 159, 207, 0.1)',
                    border: '1px solid var(--primary-color)',
                    borderRadius: 'var(--border-radius-sm)',
                    padding: '1rem',
                    marginBottom: '1rem',
                    position: 'relative'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--primary-color)', fontWeight: 'bold' }}>
                      ✨ Analyse du Coach IA
                    </div>
                    <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.4', color: 'var(--text-primary)' }}>
                      {workout.aiInsight}
                    </p>
                  </div>
                )}

                {workout.exercises.map((block, idx) => {
                  const isSuperset = block.type === 'superset';
                  const items = isSuperset ? block.items : [block];
                  return (
                  <div key={idx} style={{ backgroundColor: 'var(--bg-color)', padding: '1rem', borderRadius: 'var(--border-radius-sm)', borderLeft: isSuperset ? '4px solid var(--secondary-color)' : 'none' }}>
                    {isSuperset && <div style={{fontWeight:'bold', color:'var(--secondary-color)', marginBottom:'0.5rem'}}>🔗 Superset</div>}
                    {items.map((ex, iIdx) => {
                      const isCardio = isCardioExercise(ex, allExercises);
                      return (
                      <div key={iIdx} style={{ marginBottom: iIdx < items.length-1 ? '1.5rem' : 0 }}>
                        <h4 style={{ margin: '0 0 0.5rem 0' }}>{ex.name}{ex.equipment && ex.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.9em' }}> ({ex.equipment})</span> : ''}</h4>
                        {(() => {
                          const getSetNumber = (sets, currentIndex) => {
                            let count = 0;
                            for (let i = 0; i <= currentIndex; i++) {
                              if (!sets[i].isDropSet && !sets[i].isWarmup) count++;
                            }
                            return count;
                          };
                          
                          const cardioType = isCardio ? getCardioType(ex.name) : 'generic';
                          const cardioFields = isCardio ? getCardioFields(cardioType) : [];

                          return (
                          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px', textAlign: 'center' }}>
                            <thead>
                              <tr style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                <th style={{ padding: '0.5rem', fontWeight: '600' }}>Série</th>
                                {isCardio ? (
                                  cardioFields.map((f, fIdx) => (
                                    <th key={fIdx} style={{ padding: '0.5rem', fontWeight: '600' }}>{f.label}</th>
                                  ))
                                ) : (
                                  <>
                                    <th style={{ padding: '0.5rem', fontWeight: '600' }}>Poids (kg)</th>
                                    <th style={{ padding: '0.5rem', fontWeight: '600' }}>Reps</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {ex.sets.map((set, sIdx) => {
                                if (isCardio) {
                                  return (
                                    <tr key={sIdx} style={{
                                      backgroundColor: set.isWarmup ? 'rgba(243, 156, 18, 0.08)' : 'var(--bg-card)',
                                      color: set.isWarmup ? '#f39c12' : 'var(--text-primary)',
                                      borderRadius: 'var(--border-radius-sm)',
                                      overflow: 'hidden'
                                    }}>
                                      <td style={{ padding: '0.6rem', textAlign: 'center', fontSize: '1rem', borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px', fontWeight: '500' }}>
                                        {set.isWarmup ? '🔥 Échauffement' : `Session ${getSetNumber(ex.sets, sIdx)}`}
                                      </td>
                                      {cardioFields.map((field, fIdx) => {
                                        const isLast = fIdx === cardioFields.length - 1;
                                        let val = set[field.key] !== undefined && set[field.key] !== '' ? set[field.key] : '-';
                                        
                                        // Legacy fallback for old data
                                        if (val === '-') {
                                           if (field.key === 'duration' && set.reps !== undefined && set.reps !== '') val = set.reps;
                                           if (field.key === 'distance' && set.weight !== undefined && set.weight !== '') val = set.weight;
                                        }

                                        let suffix = '';
                                        if (val !== '-') {
                                          if (field.key === 'duration') suffix = ' min';
                                          if (field.key === 'distance') suffix = cardioType === 'rower' ? ' m' : ' km';
                                          if (field.key === 'incline') suffix = ' %';
                                          if (field.key === 'floors') suffix = ' nb';
                                          if (field.key === 'intensity') suffix = ' lvl';
                                        }

                                        return (
                                          <td key={fIdx} style={{ padding: '0.6rem', fontWeight: 'bold', ...(isLast ? { borderTopRightRadius: '6px', borderBottomRightRadius: '6px' } : {}) }}>
                                            {val}{suffix}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                }

                                if (set.isUnilateral) {
                                  return (
                                    <tr key={sIdx} style={{
                                      backgroundColor: set.isDropSet ? 'rgba(34, 197, 94, 0.08)' : (set.isWarmup ? 'rgba(243, 156, 18, 0.08)' : 'var(--bg-card)'),
                                      color: set.isDropSet ? 'var(--secondary-color)' : (set.isWarmup ? '#f39c12' : 'var(--text-primary)'),
                                      borderRadius: 'var(--border-radius-sm)',
                                      overflow: 'hidden'
                                    }}>
                                      <td style={{ padding: '0.6rem', textAlign: set.isDropSet ? 'right' : 'center', fontSize: set.isDropSet || set.isWarmup ? '0.85rem' : '1rem', borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px', fontWeight: set.isDropSet ? 'normal' : '500' }}>
                                        {set.isDropSet ? '↳ Dégressive' : (set.isWarmup ? '🔥 Échauf.' : getSetNumber(ex.sets, sIdx))} (Uni)
                                      </td>
                                      <td style={{ padding: '0.6rem', fontWeight: 'bold' }}>
                                        G: {set.weightL || 0} / D: {set.weightR || 0}
                                      </td>
                                      <td style={{ padding: '0.6rem', borderTopRightRadius: '6px', borderBottomRightRadius: '6px' }}>
                                        G: {set.repsL || 0} / D: {set.repsR || 0}
                                      </td>
                                    </tr>
                                  );
                                }

                                const weightVal = (set.weight === undefined || set.weight === null || set.weight === '') ? 0 : set.weight;
                                const repsVal = (set.reps === undefined || set.reps === null || set.reps === '') ? 0 : set.reps;
                                return (
                                  <tr key={sIdx} style={{
                                    backgroundColor: set.isDropSet ? 'rgba(34, 197, 94, 0.08)' : (set.isWarmup ? 'rgba(243, 156, 18, 0.08)' : 'var(--bg-card)'),
                                    color: set.isDropSet ? 'var(--secondary-color)' : (set.isWarmup ? '#f39c12' : 'var(--text-primary)'),
                                    borderRadius: 'var(--border-radius-sm)',
                                    overflow: 'hidden'
                                  }}>
                                    <td style={{ padding: '0.6rem', textAlign: set.isDropSet ? 'right' : 'center', fontSize: set.isDropSet || set.isWarmup ? '0.85rem' : '1rem', borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px', fontWeight: set.isDropSet ? 'normal' : '500' }}>
                                      {set.isDropSet ? '↳ Dégressive' : (set.isWarmup ? '🔥 Échauf.' : getSetNumber(ex.sets, sIdx))}
                                    </td>
                                    <td style={{ padding: '0.6rem', textAlign: 'center', fontSize: '1.05rem', fontWeight: 'bold' }}>{set.weight !== undefined && set.weight !== '' ? set.weight : (set.distance || 0)}</td>
                                    <td style={{ padding: '0.6rem', textAlign: 'center', fontSize: '1.05rem', fontWeight: 'bold', borderTopRightRadius: '6px', borderBottomRightRadius: '6px' }}>{set.reps !== undefined && set.reps !== '' ? set.reps : (set.duration || 0)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        );
                      })()}
                      </div>
                    )})}
                  </div>
                  );
                })}
              </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default History;
